<template>
    <PageSidebar>
        <template #sidebarTitle>
            <div class="space-x-3">
                <ButtonSecondaryIconSm @click="quit">
                    <ArrowLeftIcon />
                </ButtonSecondaryIconSm>
                <span>{{ headerLabel }}</span>
            </div>
        </template>
        <template #sidebarMain>
            <div class="p-6 space-y-5">
                <FormInput
                    id="fileName"
                    :title="$t('shared.name')"
                    :placeholder="$t('shared.name')"
                    v-model="file.name"
                />

                <template v-if="file.type !== PLAYER_FILE_TYPES.header">
                    <FormSelect
                        :label="$t('player.files.file_type')"
                        :options="fileTypesListWithoutHeader"
                        :value="file.type"
                        v-model="file.type"
                    />

                    <FormSelect
                        v-if="file.type === PLAYER_FILE_TYPES.video"
                        :label="$t('player.files.file_source')"
                        :options="videoSourcesList"
                        :value="file.videoSrc"
                        v-model="file.videoSrc"
                    />

                    <FormRichText
                        v-if="file.type === PLAYER_FILE_TYPES.text"
                        :title="$t('player.files.file_text')"
                        :help="$t('player.files.file_text_help')"
                        :placeholder="$t('player.files.file_text')"
                        :rows="15"
                        v-model="file.text"
                    />

                    <FormInput
                        v-if="file.type === PLAYER_FILE_TYPES.video"
                        :title="videoSrcLabel"
                        :placeholder="videoSrcPlaceholder"
                        v-model="file.src"
                        :help="videoHelp"
                    />

                    <template
                        v-if="
                            file.type !== PLAYER_FILE_TYPES.text &&
                            file.type !== PLAYER_FILE_TYPES.video
                        "
                    >
                        <FormFiles
                            :title="$t('player.files.file_source')"
                            :labels="{
                                clickToUpload: $t('shared.uploader.click_to_upload'),
                                dragAndDrop: $t('shared.uploader.drag_and_drop'),
                                fileInfo: '',
                            }"
                            :accept="file.type === PLAYER_FILE_TYPES.image ? 'image/*' : '*/*'"
                            :maxSize="10000"
                            :errorMessages="{
                                invalidSize: $t('shared.invalid.file_size_over'),
                                invalidType: $t('shared.invalid.file_type'),
                            }"
                            :single-file="true"
                            v-model="files"
                            @fileSelected="uploadFile"
                            @fileRemoved="removeFile"
                        />
                        <FormTextArea
                            :title="$t('player.files.external_link_alternative')"
                            placeholder="https://"
                            v-model="file.src"
                        />
                    </template>

                    <FormInput
                        id="fileInfo"
                        :title="$t('player.files.extra_info')"
                        placeholder="4:30 min"
                        v-model="file.info"
                    />

                    <div id="fileDescription">
                        <FormRichText
                            :title="$t('player.files.file_description')"
                            :placeholder="$t('player.files.file_description')"
                            v-model="file.description"
                            :rows="15"
                        />
                    </div>
                </template>
            </div>
            <CardToggle :title="$t('player.settings.delayed_start')">
                <div class="space-y-5">
                    <FormInputDate
                        enableTimePicker
                        canReset
                        :title="$t('player.files.start_date')"
                        :lang="lang"
                        :modelValue="file.releaseAt"
                        @valueAsDate="file.releaseAt = $event"
                    />
                    <template v-if="file.releaseAt">
                        <NotificationInfo :closeButton="false">
                            {{ $t("player.files.show_before_start_info") }}
                        </NotificationInfo>
                        <FormToggleSm
                            :title="$t('player.files.show_before_start')"
                            v-model="file.showBeforeRelease"
                        />
                    </template>
                </div>
            </CardToggle>
        </template>
        <template #sidebarFooter>
            <cSaveButton
                @save="$emit('save')"
                :disabled="!isDirty"
                :ctaEnabled="saveButtonText"
                :ctaDisabled="saveButtonText"
            />
        </template>
    </PageSidebar>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { PageSidebar } from "@easy/ui/page-components";
import { ButtonSecondaryIconSm } from "@easy/ui/buttons";
import { ArrowLeftIcon } from "@easy/ui/icons";
import { CardToggle } from "@easy/ui/cards";
import {
    FormInput,
    FormSelect,
    FormTextArea,
    FormFiles,
    FormRichText,
    makeFileModel,
    FormInputDate,
    FormToggleSm,
} from "@easy/ui/forms";
import { useSaveOrDropModal } from "@/store/useSaveOrDropModal";
import { useIsDirty } from "@/shared/utils/forms/useIsDirty";
import { PLAYER_FILE_TYPES, PLAYER_VIDEO_SOURCES } from "@/apps/player/services/players.service";
import { NotificationInfo } from "@easy/ui/notifications";
import cSaveButton from "@/shared/components/cSaveButton.vue";
import { useFocusOnQueryField } from "@/shared/utils/forms/useFocusOnQueryField";

export default {
    components: {
        PageSidebar,
        ButtonSecondaryIconSm,
        ArrowLeftIcon,
        FormInput,
        FormSelect,
        FormTextArea,
        FormFiles,
        FormRichText,
        FormInputDate,
        FormToggleSm,
        cSaveButton,
        CardToggle,
        NotificationInfo,
    },
    props: {
        // v-model:file!
        file: {
            type: Object,
            required: true,
        },
        backupData: {
            type: Object,
            required: true,
        },
        mode: {
            type: String,
            required: true,
        },
    },
    emits: ["cancel", "save", "update:file"],
    setup(props) {
        useFocusOnQueryField();
        const { isDirty } = useIsDirty(props.backupData, props.file);
        const { askToConfirmIfNeeded } = useSaveOrDropModal();

        return {
            isDirty,
            askToConfirmIfNeeded,
        };
    },
    data() {
        return {
            PLAYER_FILE_TYPES,
            PLAYER_VIDEO_SOURCES,
            files: [],
        };
    },
    watch: {
        file: {
            handler(file, oldFile) {
                if (!oldFile?.src && file?.src) {
                    this.files = [
                        makeFileModel({
                            url: file.src,
                        }),
                    ];
                }
            },
            immediate: true,
            deep: true,
        },
    },
    computed: {
        ...mapState(["globalUrl", "lang"]),
        fileTypesList() {
            return [
                { value: PLAYER_FILE_TYPES.video, label: this.$t("player.files.video") },
                { value: PLAYER_FILE_TYPES.audio, label: "Audio" },
                { value: PLAYER_FILE_TYPES.file, label: this.$t("player.files.file") },
                { value: PLAYER_FILE_TYPES.pdf, label: "PDF" },
                { value: PLAYER_FILE_TYPES.google, label: this.$t("player.files.google") },
                { value: PLAYER_FILE_TYPES.embed, label: this.$t("player.files.embed") },
                { value: PLAYER_FILE_TYPES.text, label: this.$t("player.files.text") },
                { value: PLAYER_FILE_TYPES.header, label: this.$t("player.files.header") },
                { value: PLAYER_FILE_TYPES.image, label: this.$t("player.files.image") },
            ];
        },
        videoSourcesList() {
            return [
                {
                    value: PLAYER_VIDEO_SOURCES.youtube,
                    label: "Youtube",
                    placeholderSrc: "https://www.youtube.com/watch?v=LxIR7eFw8HI",
                },
                {
                    value: PLAYER_VIDEO_SOURCES.vimeo,
                    label: "Vimeo",
                    placeholderSrc: "https://vimeo.com/754255543",
                },
                {
                    value: PLAYER_VIDEO_SOURCES.wistia,
                    label: "Wistia",
                    placeholderSrc: "https://fast.wistia.net/embed/iframe/otpvb7ezvi",
                },
                {
                    value: PLAYER_VIDEO_SOURCES.other,
                    label: this.$t("player.files.other"),
                    placeholderSrc: "https://",
                },
            ];
        },
        videoHelp() {
            if (this.file.videoSrc === PLAYER_VIDEO_SOURCES.vimeo) {
                return this.$t("player.files.vimeo_help");
            }
            if (this.file.videoSrc === PLAYER_VIDEO_SOURCES.youtube) {
                return this.$t("player.files.youtube_help");
            }
            return "";
        },
        videoSrcPlaceholder() {
            return this.videoSourcesList?.find((x) => x.value === this.file.videoSrc)
                .placeholderSrc;
        },
        fileTypesListWithoutHeader() {
            return this.fileTypesList.filter((x) => x.value !== PLAYER_FILE_TYPES.header);
        },
        headerLabel() {
            return this.mode === "create"
                ? this.file.type !== PLAYER_FILE_TYPES.header
                    ? this.$t("player.files.add_file")
                    : this.$t("player.files.add_header")
                : this.file.type !== PLAYER_FILE_TYPES.header
                  ? this.$t("player.files.edit_file")
                  : this.$t("player.files.edit_header");
        },
        videoSrcLabel() {
            return ` ${
                this.file.videoSrc === "other"
                    ? this.$t("player.files.video")
                    : this.file.videoSrc.charAt(0).toUpperCase() + this.file.videoSrc.slice(1)
            } ${this.$t("player.files.link_to")}`;
        },
        saveButtonText() {
            return this.mode === "create"
                ? this.file.type !== PLAYER_FILE_TYPES.header
                    ? this.$t("player.files.create_file")
                    : this.$t("player.files.create_header")
                : this.file.type !== PLAYER_FILE_TYPES.header
                  ? this.$t("player.files.save_file")
                  : this.$t("player.files.save_header");
        },
    },
    methods: {
        ...mapActions("player", ["uploadSrc"]),

        toggleType(type) {
            this.file.type = type;
        },
        async quit() {
            const result = await this.askToConfirmIfNeeded({
                showModal: this.isDirty,
                onConfirmChanges: () => this.$emit("save"),
            });
            if (!result) {
                this.$emit("cancel");
            }
        },
        removeFile() {
            this.file.src = "";
        },
        uploadFile($event) {
            this.uploadSrc({
                file: this.file,
                src: $event,
            });
        },
    },
};
</script>
